import {
  appConfigurationQueryOptions,
  useAppConfiguration,
} from "@/api/app-configuration.api";
import {
  simpleCampaignsQueryOptions,
  useSimpleCampaigns,
} from "@/api/campaign.api";
import { listLeadsParamsSchema, useLeadTokenDownload } from "@/api/leads.api";
import { createUsersQueryOptions } from "@/api/users.api";
import {
  LeadsTable,
  LeadsTableBody,
  LeadsTableHeader,
} from "@/components/common/LeadsTable";
import { Button } from "@/components/generic/Button";
import { Search } from "@/components/generic/Search";
import {
  SelectFilter,
  useSelectFilter,
} from "@/components/generic/SelectFilter";
import { PageContent } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import { StickyElement } from "@/components/layout/StickyElement";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import { useLeads } from "@/hooks/use-leads";
import { useHorizontalScrollSync } from "@/hooks/use-scroll-sync";
import { isAdmin as isUserAdmin } from "@/services/auth.service";
import { Lead } from "@/types/campaign";
import { mapAndExclude } from "@/utils/array-utils";
import { formatNumber } from "@/utils/number-utils";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  NavigateOptions,
  redirect,
} from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import { AnimatePresence, motion } from "framer-motion";
import { atom, getDefaultStore } from "jotai";
import { Download, LoaderCircleIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SortDescriptor } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useIntersectionObserver } from "usehooks-ts";
import { z } from "zod";

const searchSchema = listLeadsParamsSchema
  .pick({
    name: true,
    configurationName: true,
    status: true,
    responsibleId: true,
    campaignId: true,
    orderBy: true,
    desc: true,
  })
  .extend({
    createdBefore: z
      .string()
      .optional()
      .transform((value) => (value ? new Date(value) : undefined))
      .optional(),
  });

const createdBeforeAtom = atom<Date | null>(null);

export const Route = createFileRoute("/_dashboard/leads/")({
  component: RouteComponent,
  validateSearch: searchSchema.parse,
  beforeLoad: async ({ context, search, cause }) => {
    const jotaiDefaultStore = getDefaultStore();

    if (cause !== "preload") {
      // Hide createdBefore from search and update atom
      if (search.createdBefore) {
        jotaiDefaultStore.set(createdBeforeAtom, search.createdBefore);
        throw redirect({
          replace: true,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          search: { ...search, createdBefore: undefined },
        });
      }

      // Reset createdBefore if not in search
      if (!jotaiDefaultStore.get(createdBeforeAtom)) {
        jotaiDefaultStore.set(createdBeforeAtom, new Date());
      }
    }

    const usersQueryOptions = createUsersQueryOptions({ nameFilter: "" });

    const [appConfig, simpleCampaigns, usersQueryData] = await Promise.all([
      context.queryClient.ensureQueryData(appConfigurationQueryOptions),
      context.queryClient.ensureQueryData({
        ...simpleCampaignsQueryOptions,
        revalidateIfStale: false,
      }),
      context.queryClient.ensureInfiniteQueryData({
        ...usersQueryOptions,
        revalidateIfStale: false,
      }),
    ]);

    // Sanitize and update search
    {
      function filterSet<T>(values: Set<T>, validValues: Set<T>) {
        const newValues = new Set<T>();
        for (const value of values) {
          if (validValues.has(value)) {
            newValues.add(value);
          }
        }
        return newValues;
      }

      function compareSets<T>(a: Set<T>, b: Set<T>) {
        if (a.size !== b.size) return false;
        for (const value of a) {
          if (!b.has(value)) return false;
        }
        return true;
      }

      const newSearch: typeof search = {};

      if (search.configurationName) {
        const values = new Set(search.configurationName);
        const newValues = filterSet(
          values,
          new Set(["Guld", "Sølv", "Bronze"]),
        );
        if (!compareSets(values, newValues)) {
          newSearch.configurationName = newValues.size
            ? [...newValues]
            : undefined;
        }
      }

      if (search.status === undefined) {
        newSearch.status = ["New", "FollowUpDue"];
      } else {
        const values = new Set(search.status);
        const newValues = filterSet(
          values,
          new Set<string>(appConfig.leadStatuses.map(({ name }) => name)),
        );
        if (!compareSets(values, newValues)) {
          newSearch.status = [...newValues];
        }
      }

      if (search.responsibleId) {
        const values = new Set(search.responsibleId);
        const newValues = filterSet(
          values,
          new Set<string>(
            usersQueryData.pages.flatMap((page) =>
              page.results.map(({ id }) => id),
            ),
          ),
        );
        if (!compareSets(values, newValues)) {
          newSearch.responsibleId = newValues.size ? [...newValues] : undefined;
        }
      }

      if (search.campaignId) {
        const values = new Set(search.campaignId);
        const newValues = filterSet(
          values,
          new Set<number>(simpleCampaigns.map(({ id }) => id)),
        );
        if (!compareSets(values, newValues)) {
          newSearch.campaignId = newValues.size ? [...newValues] : undefined;
        }
      }

      if (!new Set<unknown>(["created", "followUpTime"]).has(search.orderBy)) {
        newSearch.orderBy = "followUpTime";
      }

      if (search.desc === undefined) {
        newSearch.desc = false;
      }

      if (Object.keys(newSearch).length) {
        throw redirect({
          replace: true,
          from: Route.fullPath,
          search: {
            ...search,
            ...newSearch,
          },
        });
      }
    }

    return {
      createdBefore: jotaiDefaultStore.get(createdBeforeAtom) ?? new Date(),
      useUsers: () => {
        const query = useInfiniteQuery({
          ...usersQueryOptions,
          throwOnError: false,
          initialData: usersQueryData,
        });

        const users = useMemo(
          () => query.data.pages.flatMap((page) => page.results),
          [query.data.pages],
        );

        return { users, query };
      },
    };
  },
  onLeave: () => {
    getDefaultStore().set(createdBeforeAtom, null);
  },
  staticData: {
    meta: {
      title: "Leads",
      isTitleRoot: true,
    },
  },
});

function RouteComponent() {
  const { user, createdBefore, trialPeriod, useUsers } =
    Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  useHorizontalScrollSync(headerScrollRef, bodyScrollRef);

  const isAdmin = useMemo(() => isUserAdmin(user), [user]);

  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

  const allSimpleCampaigns = useSimpleCampaigns();
  const simpleCampaigns = useMemo(
    () =>
      allSimpleCampaigns?.filter(
        (simpleCampaign) => simpleCampaign.status !== "Inactive",
      ),
    [allSimpleCampaigns],
  );

  const { data: appConfig } = useAppConfiguration();

  const { users } = useUsers();

  const [searchString, setSearchString] = useState("");

  const configurationFilter = useSelectFilter({
    items: useMemo(
      () => [
        { key: "Guld", label: "Guld", value: "Guld" },
        { key: "Sølv", label: "Sølv", value: "Sølv" },
        { key: "Bronze", label: "Bronze", value: "Bronze" },
      ],
      [],
    ),
  });

  const statusFilter = useSelectFilter({
    items: useMemo(
      () =>
        mapAndExclude(appConfig?.leadStatuses ?? [], (exclude, status) =>
          status.filterable
            ? {
                key: status.name,
                label: status.displayName,
                value: status.name,
              }
            : exclude,
        ),
      [appConfig],
    ),
  });

  const responsibleFilter = useSelectFilter({
    items: useMemo(
      () =>
        users.map((user) => ({
          key: user.id,
          label: user.name,
          value: user.id,
        })),
      [users],
    ),
  });

  const campaignFilter = useSelectFilter({
    items: useMemo(
      () =>
        simpleCampaigns?.map((campaign) => ({
          key: campaign.id,
          label: campaign.name,
          value: campaign.id,
        })) ?? [],
      [simpleCampaigns],
    ),
  });

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "followUpTime",
    direction: "ascending",
  });

  let skipSearchUpdate = false;

  useEffect(() => {
    if (search.name) {
      setSearchString(search.name);
    }

    if (search.configurationName) {
      configurationFilter.setSelectedKeys(new Set(search.configurationName));
    }

    if (search.status) {
      statusFilter.setSelectedKeys(new Set(search.status));
    }

    if (search.responsibleId) {
      responsibleFilter.setSelectedKeys(new Set(search.responsibleId));
    }

    if (search.campaignId) {
      campaignFilter.setSelectedKeys(new Set(search.campaignId));
    }

    if (search.orderBy || search.desc !== undefined) {
      setSortDescriptor({
        column: search.orderBy ?? "followUpTime",
        direction: (search.desc ?? false) ? "descending" : "ascending",
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    skipSearchUpdate = true;
  }, []);

  const newSearch = useMemo(() => {
    return {
      name: searchString || undefined,
      configurationName: configurationFilter.selectedValues.length
        ? configurationFilter.selectedValues
        : undefined,
      status: statusFilter.selectedValues.length
        ? statusFilter.selectedValues
        : undefined,
      responsibleId: responsibleFilter.selectedValues.length
        ? responsibleFilter.selectedValues
        : undefined,
      campaignId: campaignFilter.selectedValues.length
        ? campaignFilter.selectedValues
        : undefined,
      orderBy: sortDescriptor.column.toString(),
      desc: sortDescriptor.direction === "descending",
    } satisfies typeof search;
  }, [
    searchString,
    configurationFilter.selectedValues,
    statusFilter.selectedValues,
    responsibleFilter.selectedValues,
    campaignFilter.selectedValues,
    sortDescriptor.column,
    sortDescriptor.direction,
  ]);

  const debouncedNewSearch = useDebounce(newSearch, 300);

  useEffect(() => {
    if (skipSearchUpdate) return;

    let diffFound = false;

    for (const key of Object.keys(newSearch) as (keyof typeof newSearch)[]) {
      const newValue = newSearch[key];
      const oldValue = search[key];
      if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
        diffFound = true;
        break;
      }
    }

    if (diffFound) {
      navigate({
        from: Route.fullPath,
        search: {
          ...search,
          ...newSearch,
          createdBefore: new Date(),
        },
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipSearchUpdate, debouncedNewSearch]);

  const { leads, leadsQuery, pushMessageLeads } = useLeads({
    params: {
      ...search,
      createdBefore,
    },
  });

  const totalLeadsCount = useMemo(() => {
    return (
      (leadsQuery.data?.pages.at(0)?.resultsFound ?? 0) +
      pushMessageLeads.length
    );
  }, [leadsQuery.data?.pages, pushMessageLeads.length]);

  const leadsQueryErrorText = useMemo(() => {
    if (leadsQuery.data?.pages.length !== 1) return "";
    const [{ error }] = leadsQuery.data.pages;
    return error?.message ?? "";
  }, [leadsQuery.data?.pages]);

  const tableColumnsToOmit = useMemo(
    () => ({ responsible: !isAdmin }),
    [isAdmin],
  );

  const buildLeadLink = useCallback(
    (lead: Lead): NavigateOptions => ({
      to: "/player/$id",
      params: { id: lead.id },
      search: {
        ...search,
        createdBefore,
        returnTo: "leads",
      },
    }),
    [search, createdBefore],
  );

  const tableScrollRef = useRef<HTMLDivElement>(null);

  const tableTopIntersectionObserver = useIntersectionObserver({
    root: tableScrollRef.current,
  });

  const tableBottomIntersectionObserver = useIntersectionObserver({
    root: tableScrollRef.current,
    rootMargin: "24px",
  });

  useEffect(() => {
    if (
      !tableBottomIntersectionObserver.isIntersecting ||
      leadsQuery.isFetching
    )
      return;

    leadsQuery.fetchNextPage();
  }, [
    tableBottomIntersectionObserver.isIntersecting,
    leadsQuery,
    leadsQuery.isFetching,
  ]);

  const { download: downLoadFile, isDownloading } = useLeadTokenDownload({
    ...search,
    createdBefore,
  });

  return (
    <>
      <PageHeader
        title="Leads"
        actions={[
          <Button
            key="afspil"
            intent="primary"
            size="lg"
            {...(leads.length
              ? trialPeriod?.isExpired
                ? { onPress: () => setShowTrialExpiredModal(true) }
                : { link: buildLeadLink(leads[0]) }
              : { isDisabled: true })}
          >
            Vis leadkort{" "}
            {leadsQuery.data &&
              `(${formatNumber(Math.min(totalLeadsCount, 1000))}${totalLeadsCount > 1000 ? "+" : ""})`}
          </Button>,
        ]}
      />

      <StickyElement
        id="scroll-headers"
        className="overflow-x-scroll"
        stickyProps={{
          className: "bg-theme-background 0",
        }}
      >
        <div className="px-8 xl:px-14">
          <div className="my-4 flex items-center gap-x-6">
            <Search
              className="-mx-2 min-w-[150px] grow"
              value={searchString}
              onChange={(e) => setSearchString(e.currentTarget.value)}
            />

            <div className="flex items-center gap-x-6">
              <SelectFilter
                buttonLabel="Type"
                selectionMode="multiple"
                items={configurationFilter.items}
                selectedKeys={configurationFilter.selectedKeys}
                onSelectionChange={configurationFilter.setSelectedKeys}
              />

              <SelectFilter
                buttonLabel="Status"
                selectionMode="multiple"
                items={statusFilter.items}
                selectedKeys={statusFilter.selectedKeys}
                onSelectionChange={statusFilter.setSelectedKeys}
              />

              <SelectFilter
                buttonLabel="Kampagne"
                selectionMode="multiple"
                items={campaignFilter.items}
                selectedKeys={campaignFilter.selectedKeys}
                onSelectionChange={campaignFilter.setSelectedKeys}
              />

              {isAdmin && (
                <SelectFilter
                  buttonLabel="Ansvarlig"
                  selectionMode="multiple"
                  items={responsibleFilter.items}
                  selectedKeys={responsibleFilter.selectedKeys}
                  onSelectionChange={responsibleFilter.setSelectedKeys}
                />
              )}
              <Button
                isDisabled={leads.length === 0 || isDownloading}
                onClick={() => {
                  downLoadFile();
                }}
                className="-mr-2 -ml-3 h-8 min-w-0 pr-2 pl-3 text-[11px]/[16px] font-medium"
                intent="tertiary"
                size="sm"
              >
                <div
                  className={twMerge(
                    "flex items-center gap-x-2",
                    (isDownloading || leads.length == 0) &&
                      "text-theme-on-background-variant",
                  )}
                >
                  <span className="text-[11px]">Download</span>
                  {isDownloading ? (
                    <LoaderCircleIcon className="size-4 animate-spin" />
                  ) : (
                    <Download size={16} />
                  )}
                </div>
              </Button>
            </div>
          </div>

          <div className="border-separator mb-3" />
        </div>
      </StickyElement>

      <PageContent className="flex grow flex-col">
        <LeadsTable
          className="max-w-full grow"
          omitColumns={tableColumnsToOmit}
          leads={leads}
          campaigns={allSimpleCampaigns}
          users={users}
          appConfig={appConfig}
          sortDescriptor={sortDescriptor}
          setSortDescriptor={setSortDescriptor}
          {...(trialPeriod?.isExpired
            ? {
                onClickLead: () => setShowTrialExpiredModal(true),
              }
            : {
                buildLeadLink,
              })}
        >
          <div ref={tableTopIntersectionObserver.ref} />

          <StickyElement
            ref={headerScrollRef}
            id="scroll-headers"
            className="overflow-x-scroll"
            stickyProps={{
              className: "bg-theme-background overflow-scroll",
            }}
          >
            <LeadsTableHeader className="sticky" />
          </StickyElement>

          <LeadsTableBody
            id="scroll-headers"
            ref={bodyScrollRef}
            className="min-w-0 overflow-x-scroll"
          />

          <div className="grid h-14 shrink-0 grow px-14 py-5 *:col-start-1 *:row-start-1">
            <AnimatePresence>
              {(() => {
                let key = "";
                let text = "";

                if (leadsQuery.isFetching) {
                  key = "loading";
                  text = "Henter leads...";
                } else if (!leads.length) {
                  if (leadsQueryErrorText) {
                    key = "error";
                    text = leadsQueryErrorText;
                  } else {
                    key = "no-results";
                    text = "Ingen leads fundet";
                  }
                }

                if (!key) return null;

                return (
                  <motion.div
                    key={key}
                    className="flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <span
                      className={twMerge(
                        "text-theme-on-background-variant text-center text-xs font-medium",
                        key === "loading" && "animate-pulse",
                      )}
                    >
                      {text}
                    </span>
                  </motion.div>
                );
              })()}
            </AnimatePresence>
          </div>

          {!leadsQuery.isFetching && leadsQuery.hasNextPage && (
            <div ref={tableBottomIntersectionObserver.ref} />
          )}
        </LeadsTable>
      </PageContent>

      <TrialCTAModal
        isOpen={showTrialExpiredModal}
        onOpenChange={setShowTrialExpiredModal}
        title="Din prøveperiode er udløbet. Kontakt os for at få adgang til Lasso Sales uden begrænsninger."
      />
    </>
  );
}
